import {
  Battery100Icon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  MapPinIcon,
  SignalIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { apiService, extractApiErrorMessage } from '../../services/api';
import { showIntegrationErrorToast } from '../../utils/integrationErrorToast';
import { isBhphDeal } from '../../utils/dealType';
import { normalizeRole } from '../../utils/roles';
import { ResponsiveModal, ModalActionFooter } from '../ui/Modal';

interface GPSDevice {
  id: string;
  device_id: string;
  imei?: string;
  provider: string;
  device_type: string;
  model?: string;
  status: string;
  last_ping?: string;
  battery_level?: number;
  signal_strength?: number;
  disable_enabled: boolean;
  // Per-vehicle customer consent to remote starter-interrupt (WS1c-2 gate).
  // A remote disable is fail-closed: hidden in the UI until this is on file.
  disable_consent_on_file: boolean;
  installation_date?: string;
  installed_by?: string;
}

interface GPSDeviceManagerProps {
  dealId: string;
  dealType: string;
  vehicleId: string;
  customerId: string;
  onDeviceChange?: (device: GPSDevice | null) => void;
}

const GPSDeviceManager: React.FC<GPSDeviceManagerProps> = ({
  dealId,
  dealType,
  vehicleId,
  customerId: _customerId,
  onDeviceChange,
}) => {
  const [device, setDevice] = useState<GPSDevice | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [availableDevices, setAvailableDevices] = useState<GPSDevice[]>([]);
  const [disableLoading, setDisableLoading] = useState(false);
  const [consentLoading, setConsentLoading] = useState(false);
  // Pending remote action awaiting confirmation in the themed modal (null = none)
  const [confirmAction, setConfirmAction] = useState<'disable' | 'enable' | null>(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  // Recording per-vehicle starter-interrupt consent is a manager-level liability
  // action (backend route is manager-gated); hide the control from reps. Mirror
  // the backend `require_manager` set exactly, normalizing legacy role aliases.
  const canManageConsent = [
    'system_admin',
    'general_manager',
    'sales_manager',
    'finance_manager',
  ].includes(normalizeRole(user?.role));

  // Where the dealer configures GPS provider credentials, and where to get them.
  const GPS_SETUP_PATH = '/settings?category=gps';
  const GPS_KEY_URL = 'https://globaltrack.sarekon.com';

  // Only show GPS management for BHPH deals. Normalized here rather than trusting
  // the caller to pre-format: the API returns deal_type lowercase ('bhph').
  const showGPSManager = isBhphDeal(dealType);

  const fetchAssignedDevice = useCallback(async () => {
    try {
      setLoading(true);
      // Try to get GPS device for this vehicle
      const devices = await apiService.get<GPSDevice[]>(`/gps/devices?vehicle_id=${vehicleId}`);
      if (devices && devices.length > 0) {
        setDevice(devices[0]);
        onDeviceChange?.(devices[0]);
      }
    } catch (error) {
      console.error('Error fetching GPS device:', error);
    } finally {
      setLoading(false);
    }
  }, [vehicleId, onDeviceChange]);

  useEffect(() => {
    if (showGPSManager) {
      fetchAssignedDevice();
    }
  }, [dealId, vehicleId, showGPSManager, fetchAssignedDevice]);

  const fetchAvailableDevices = async () => {
    try {
      const devices = await apiService.get<GPSDevice[]>(
        '/gps/devices?status=active&unassigned=true'
      );
      setAvailableDevices(devices || []);
    } catch (error) {
      console.error('Error fetching available devices:', error);
      setAvailableDevices([]);
    }
  };

  const handleAssignDevice = async (deviceId: string) => {
    try {
      await apiService.patch(`/gps/devices/${deviceId}`, {
        vehicle_id: vehicleId,
      });

      setShowAssignModal(false);
      fetchAssignedDevice();
    } catch (error) {
      console.error('Error assigning GPS device:', error);
      toast.error('Failed to assign GPS device');
    }
  };

  // The remote disable/enable endpoint requires { device_id, action, reason }
  // (see GPSDisableEventCreate / the GPSDashboard sibling). Enable is the SAME
  // endpoint with action:'enable' — there is no separate /enable route.
  const handleRemoteDisable = async (reason: string = 'payment_late') => {
    if (!device) {
      setConfirmAction(null);
      return;
    }

    try {
      setDisableLoading(true);
      await apiService.post(`/gps/devices/${device.id}/disable`, {
        device_id: device.id,
        action: 'disable',
        reason,
        compliance_notes: `Disabled via deal ${dealId}`,
      });

      toast.success('Vehicle has been remotely disabled');
      fetchAssignedDevice(); // Refresh status
    } catch (error) {
      console.error('Error disabling vehicle:', error);
      // Honest reason + an actionable link to set up GPS credentials when the
      // provider isn't configured (otherwise a plain error).
      showIntegrationErrorToast(error, {
        navigate,
        setupPath: GPS_SETUP_PATH,
        setupLabel: 'Set up GPS credentials',
        fallbackMessage: 'Failed to disable vehicle',
        getKeyUrl: GPS_KEY_URL,
        getKeyLabel: 'Get them from Global Track',
      });
    } finally {
      setDisableLoading(false);
      setConfirmAction(null);
    }
  };

  // Record (or revoke) per-vehicle customer consent to remote starter-interrupt.
  // This is the ONLY write path for disable_consent_on_file (manager-gated server
  // side); revoking re-closes the gate so the Disable control disappears.
  const handleConsentChange = async (value: boolean) => {
    if (!device) return;
    try {
      setConsentLoading(true);
      await apiService.post(`/gps/devices/${device.id}/consent`, {
        consent_on_file: value,
      });
      toast.success(value ? 'Customer consent recorded' : 'Customer consent revoked');
      fetchAssignedDevice(); // Refresh — flips the Disable control on/off
    } catch (error) {
      console.error('Error updating starter-interrupt consent:', error);
      toast.error(extractApiErrorMessage(error, 'Failed to update consent'));
    } finally {
      setConsentLoading(false);
    }
  };

  const handleRemoteEnable = async () => {
    if (!device) {
      setConfirmAction(null);
      return;
    }

    try {
      setDisableLoading(true);
      await apiService.post(`/gps/devices/${device.id}/disable`, {
        device_id: device.id,
        action: 'enable',
        reason: 'manual_override',
        compliance_notes: `Enabled via deal ${dealId}`,
      });

      toast.success('Vehicle has been enabled');
      fetchAssignedDevice(); // Refresh status
    } catch (error) {
      console.error('Error enabling vehicle:', error);
      showIntegrationErrorToast(error, {
        navigate,
        setupPath: GPS_SETUP_PATH,
        setupLabel: 'Set up GPS credentials',
        fallbackMessage: 'Failed to enable vehicle',
        getKeyUrl: GPS_KEY_URL,
        getKeyLabel: 'Get them from Global Track',
      });
    } finally {
      setDisableLoading(false);
      setConfirmAction(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'text-green-600 bg-green-100';
      case 'disabled':
        return 'text-red-600 bg-red-100';
      case 'offline':
        return 'text-gray-600 bg-gray-100';
      case 'maintenance':
        return 'text-yellow-600 bg-yellow-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return CheckCircleIcon;
      case 'disabled':
        return XCircleIcon;
      case 'offline':
        return ExclamationTriangleIcon;
      default:
        return CheckCircleIcon;
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!showGPSManager) {
    return null;
  }

  if (loading) {
    return (
      <div className="border rounded-lg p-4">
        <div className="flex items-center">
          <MapPinIcon className="h-5 w-5 text-gray-400 mr-2" />
          <h3 className="text-sm font-medium text-gray-900">GPS Tracking</h3>
        </div>
        <div className="mt-2">
          <div className="animate-pulse bg-gray-200 h-4 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="border rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center">
          <MapPinIcon className="h-5 w-5 text-gray-400 mr-2" />
          <h3 className="text-sm font-medium text-gray-900">GPS Tracking Device</h3>
        </div>
        {!device && (
          <button
            className="text-sm text-blue-600 hover:text-blue-800"
            onClick={() => {
              fetchAvailableDevices();
              setShowAssignModal(true);
            }}
          >
            Assign Device
          </button>
        )}
      </div>

      {device ? (
        <div className="space-y-3">
          {/* Device Status */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(device.status)}`}
              >
                {React.createElement(getStatusIcon(device.status), { className: 'h-3 w-3 mr-1' })}
                {device.status.replace('_', ' ').toUpperCase()}
              </div>
              <span className="text-sm text-gray-600">Device ID: {device.device_id}</span>
            </div>

            {/* Remote Controls */}
            <div className="flex space-x-2">
              {/* Fail-closed: a remote disable requires per-vehicle consent on
                  file (WS1c-2 gate). Without it the backend returns 403, so the
                  control is hidden rather than offered-then-denied. */}
              {device.status === 'active' &&
                device.disable_enabled &&
                device.disable_consent_on_file && (
                  <button
                    className="text-xs bg-red-600 text-white px-2 py-1 rounded hover:bg-red-700 disabled:opacity-50"
                    onClick={() => setConfirmAction('disable')}
                    disabled={disableLoading}
                  >
                    {disableLoading ? 'Disabling...' : 'Disable'}
                  </button>
                )}
              {device.status === 'disabled' && (
                <button
                  className="text-xs bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700 disabled:opacity-50"
                  onClick={() => setConfirmAction('enable')}
                  disabled={disableLoading}
                >
                  {disableLoading ? 'Enabling...' : 'Enable'}
                </button>
              )}
            </div>
          </div>

          {/* Starter-interrupt authorization — only relevant when the device
              supports remote disable. Shows consent state; managers can record
              or revoke the per-vehicle customer consent the gate requires. */}
          {device.disable_enabled && (
            <div className="flex items-center justify-between rounded bg-gray-50 px-2.5 py-1.5">
              <span className="text-xs text-gray-600">
                Remote-disable consent:{' '}
                <span
                  className={
                    device.disable_consent_on_file
                      ? 'font-medium text-green-700'
                      : 'font-medium text-amber-700'
                  }
                >
                  {device.disable_consent_on_file ? 'On file' : 'Not on file'}
                </span>
              </span>
              {canManageConsent && (
                <button
                  type="button"
                  className="text-xs font-semibold text-blue-600 hover:text-blue-800 disabled:opacity-50"
                  onClick={() => handleConsentChange(!device.disable_consent_on_file)}
                  disabled={consentLoading}
                >
                  {device.disable_consent_on_file ? 'Revoke consent' : 'Record consent'}
                </button>
              )}
            </div>
          )}

          {/* Device Details */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-900">Provider:</span>
              <span className="ml-1 text-gray-600 capitalize">{device.provider}</span>
            </div>
            <div>
              <span className="font-medium text-gray-900">Type:</span>
              <span className="ml-1 text-gray-600 capitalize">
                {device.device_type.replace('_', ' ')}
              </span>
            </div>
            {device.imei && (
              <div>
                <span className="font-medium text-gray-900">IMEI:</span>
                <span className="ml-1 text-gray-600 font-mono">{device.imei}</span>
              </div>
            )}
            <div>
              <span className="font-medium text-gray-900">Last Ping:</span>
              <span className="ml-1 text-gray-600">{formatDate(device.last_ping)}</span>
            </div>
          </div>

          {/* Device Health */}
          {(device.battery_level !== null || device.signal_strength !== null) && (
            <div className="flex space-x-4 pt-2 border-t">
              {device.battery_level !== null && (
                <div className="flex items-center">
                  <Battery100Icon className="h-4 w-4 text-gray-400 mr-1" />
                  <span className="text-sm text-gray-600">Battery: {device.battery_level}%</span>
                </div>
              )}
              {device.signal_strength !== null && (
                <div className="flex items-center">
                  <SignalIcon className="h-4 w-4 text-gray-400 mr-1" />
                  <span className="text-sm text-gray-600">Signal: {device.signal_strength}%</span>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-4">
          <MapPinIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-600 mb-3">No GPS device assigned to this vehicle</p>
          <button
            className="text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
            onClick={() => {
              fetchAvailableDevices();
              setShowAssignModal(true);
            }}
          >
            Assign GPS Device
          </button>
        </div>
      )}

      {/* Assign Device Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Assign GPS Device</h3>

            <div className="max-h-60 overflow-y-auto space-y-2">
              {availableDevices.length === 0 ? (
                <p className="text-sm text-gray-600">No available GPS devices found</p>
              ) : (
                availableDevices.map((availableDevice) => (
                  <div
                    key={availableDevice.id}
                    className="border rounded-lg p-3 hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleAssignDevice(availableDevice.id)}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-gray-900">{availableDevice.device_id}</p>
                        <p className="text-sm text-gray-600 capitalize">
                          {availableDevice.provider} -{' '}
                          {availableDevice.device_type.replace('_', ' ')}
                        </p>
                        {availableDevice.model && (
                          <p className="text-xs text-gray-500">{availableDevice.model}</p>
                        )}
                      </div>
                      <span
                        className={`inline-flex px-2 py-1 text-xs rounded-full ${getStatusColor(availableDevice.status)}`}
                      >
                        {availableDevice.status.toUpperCase()}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="flex justify-end space-x-2 mt-4">
              <button
                className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                onClick={() => setShowAssignModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remote disable/enable confirmation (themed — replaces window.confirm) */}
      <ResponsiveModal
        isOpen={confirmAction !== null}
        onClose={() => setConfirmAction(null)}
        size="xs"
        title={confirmAction === 'enable' ? 'Enable Vehicle' : 'Disable Vehicle'}
      >
        <p className="text-sm text-gray-600 px-1">
          {confirmAction === 'enable'
            ? 'Are you sure you want to re-enable this vehicle? This will send a remote enable command to the GPS device.'
            : 'Are you sure you want to remotely disable this vehicle? This will send a remote disable command to the GPS device.'}
        </p>
        <ModalActionFooter
          primaryAction={{
            label: confirmAction === 'enable' ? 'Enable Vehicle' : 'Disable Vehicle',
            onClick: () =>
              confirmAction === 'enable' ? handleRemoteEnable() : handleRemoteDisable(),
            loading: disableLoading,
            variant: confirmAction === 'enable' ? 'primary' : 'danger',
          }}
          secondaryAction={{
            label: 'Cancel',
            onClick: () => setConfirmAction(null),
          }}
        />
      </ResponsiveModal>
    </div>
  );
};

export default GPSDeviceManager;
